import { DEFAULT_COMPANY_TIME_ZONE, nextZonedDay, zonedDaysBetween, zonedParts } from '@dbrain/shared';

const TOKEN_UNITS = { tr: ['B', 'Mn', 'Mlr', 'Tn'], en: ['K', 'M', 'B', 'T'] } as const;

/**
 * Token amounts (D-103): plain below a thousand, then short with the unit: 6 B, 668,8 B, 9,2 Mn, 1,92 Mlr
 * (6K, 9.2M, 1.92B in English). One decimal for thousands and millions, two from a billion; never raw digits
 * such as "6.078". The unit is held to the number by a no-break space in Turkish.
 */
export function formatTokens(value: number, locale: string): string {
  const n = Math.round(value);
  if (Math.abs(n) < 1000) return n.toLocaleString(locale);
  const en = locale.startsWith('en');
  const units = en ? TOKEN_UNITS.en : TOKEN_UNITS.tr;
  let power = Math.min(Math.floor(Math.log10(Math.abs(n)) / 3), units.length);
  const digits = (p: number) => (p >= 3 ? 2 : 1);
  let shown = Number((n / 1000 ** power).toFixed(digits(power)));
  // 999.950 rounds to 1.000 B: that is 1 Mn
  if (Math.abs(shown) >= 1000 && power < units.length) {
    power += 1;
    shown = Number((n / 1000 ** power).toFixed(digits(power)));
  }
  const text = new Intl.NumberFormat(locale, { maximumFractionDigits: digits(power) }).format(shown);
  return en ? `${text}${units[power - 1]}` : `${text}\u00a0${units[power - 1]}`;
}

/**
 * The Turkish ablative ending a number takes when read aloud ("%75'ten", "%70'ten", "%80'den", "%90'dan"),
 * with its apostrophe; by the last non-zero digit's word, or the tens' word for round tens.
 */
export function turkishAblative(value: number): string {
  const n = Math.abs(Math.round(value));
  const ones = ['', 'den', 'den', 'ten', 'ten', 'ten', 'dan', 'den', 'den', 'dan'];
  const tens = ['', 'dan', 'den', 'dan', 'tan', 'den', 'tan', 'ten', 'den', 'dan'];
  if (n % 10 !== 0) return `'${ones[n % 10]}`;
  if (n % 100 !== 0) return `'${tens[(n % 100) / 10]}`;
  // yüz, bin, milyon (and sıfır)
  return n === 0 ? "'dan" : "'den";
}

/**
 * When something happened, for people (D-118): "Bugün 10:12", "Dün 23:49", "27 Eki", "27 Eki 2025";
 * `lower` for the middle of a sentence ("Son kullanım: dün 23:49"). Days and times are the company's (D-119).
 */
export function humanTime(
  iso: string,
  locale: string,
  t: (key: string, options?: Record<string, unknown>) => string,
  lower = false,
  timeZone = DEFAULT_COMPANY_TIME_ZONE,
): string {
  const at = new Date(iso);
  const now = new Date();
  const days = zonedDaysBetween(at, now, timeZone);
  const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
  const sameYear = zonedParts(at, timeZone).year === zonedParts(now, timeZone).year;
  const text =
    days === 0
      ? t('time.today', { time })
      : days === 1
        ? t('time.yesterdayAt', { time })
        : at.toLocaleDateString(locale, sameYear ? { day: 'numeric', month: 'short', timeZone } : { day: 'numeric', month: 'short', year: 'numeric', timeZone });
  return lower ? text.charAt(0).toLocaleLowerCase(locale) + text.slice(1) : text;
}

/** Counts (calls, runs, loads, events): whole numbers with the thousands separator (4.231). */
export function formatCount(value: number, locale: string): string {
  return Math.round(value).toLocaleString(locale);
}

/** Money with its currency (₺1.284,60); "—" when no price is set for the models used. */
export function formatMoney(micros: number, currency: string, locale: string, priced = true): string {
  if (!priced) return '—';
  return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(micros / 1e6);
}

/** A duration such as "8 dk 9 sn" (8 min 9 s); "—" for nothing. */
export function formatSeconds(seconds: number, locale: string): string {
  const total = Math.round(seconds);
  if (total <= 0) return '—';
  const en = locale.startsWith('en');
  const [h, m, s] = [Math.floor(total / 3600), Math.floor((total % 3600) / 60), total % 60];
  const parts: string[] = [];
  if (h > 0) parts.push(en ? `${h} h` : `${h} sa`);
  if (m > 0) parts.push(en ? `${m} min` : `${m} dk`);
  if (s > 0 && h === 0) parts.push(en ? `${s} s` : `${s} sn`);
  return parts.join(' ');
}

/** Bytes as people read them: 0 B, 337,4 KB, 2,1 GB. */
export function formatBytes(bytes: number, locale: string): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toLocaleString(locale, { maximumFractionDigits: unit === 0 ? 0 : 1 })}\u00a0${units[unit]}`;
}

/** A share as a percentage: %71 in Turkish, 71% in English. */
export function formatPercent(part: number, whole: number, locale: string, digits = 0): string {
  const share = whole > 0 ? part / whole : 0;
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(share);
}

/**
 * When someone last signed in (D-108): today and yesterday with the time, 2–6 days "3 gün önce", up to 30 days
 * the date, older again in days and marked stale (amber). Null: never. The company's days (D-119).
 */
export type LastSignIn =
  | { kind: 'never' }
  | { kind: 'today'; time: string }
  | { kind: 'yesterday'; time: string }
  | { kind: 'days'; days: number; stale: boolean }
  | { kind: 'date'; date: string };

export function lastSignIn(iso: string | null, locale: string, now = new Date(), timeZone = DEFAULT_COMPANY_TIME_ZONE): LastSignIn {
  if (!iso) return { kind: 'never' };
  const at = new Date(iso);
  const days = zonedDaysBetween(at, now, timeZone);
  const time = at.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
  if (days <= 0) return { kind: 'today', time };
  if (days === 1) return { kind: 'yesterday', time };
  if (days < 7) return { kind: 'days', days, stale: false };
  if (days <= 30) {
    const sameYear = zonedParts(at, timeZone).year === zonedParts(now, timeZone).year;
    return { kind: 'date', date: at.toLocaleDateString(locale, sameYear ? { day: 'numeric', month: 'long', timeZone } : { day: 'numeric', month: 'long', year: 'numeric', timeZone }) };
  }
  return { kind: 'days', days, stale: true };
}

const UNITS = ['da', 'de', 'de', 'te', 'te', 'te', 'da', 'de', 'de', 'da'];
const TENS: Record<number, string> = { 10: 'da', 20: 'de', 30: 'da', 40: 'ta', 50: 'de' };

/** The ending a clock time takes in Turkish, read aloud: 03:00'te, 06:00'da, 10:30'da, 00:00'da. */
export function trLocative(time: string): string {
  const [hours = 0, minutes = 0] = time.split(/[:.]/).map(Number);
  const spoken = minutes > 0 ? minutes : hours;
  if (spoken % 10 !== 0) return UNITS[spoken % 10] ?? 'de';
  return TENS[spoken] ?? (spoken === 0 ? 'da' : 'de');
}

const TENS_LOCATIVE = ['', 'da', 'de', 'da', 'ta', 'de', 'ta', 'te', 'de', 'da'];

/** The ending a whole number takes in Turkish, read aloud, by its last spoken word: 2026'da, 2027'de, 2030'da, 2000'de. */
export function numberLocative(value: number): string {
  const n = Math.abs(Math.round(value));
  if (n % 10 !== 0) return UNITS[n % 10] ?? 'de';
  if (n % 100 !== 0) return TENS_LOCATIVE[(n % 100) / 10] ?? 'de';
  return n === 0 ? 'da' : 'de';
}

/**
 * The ending a short token amount takes in Turkish, read aloud (BaglamNormal: "98,1 B’de"): bin → binde,
 * milyon, milyar, trilyon → -da, a plain number by its last spoken word (723’te, 720’de, 700’de).
 */
export function tokensLocative(text: string): string {
  const unit = text.trim().split(/\s/).pop() ?? '';
  if (unit === 'B') return 'de';
  if (unit === 'Mn' || unit === 'Mlr' || unit === 'Tn') return 'da';
  return numberLocative(Number(unit.replace(/\./g, '').replace(',', '.')) || 0);
}

/** Where a member stands today (KotaLimitler.dc.html): red and "Doldu" at the budget, amber "Sınıra yaklaştı" from 80%. */
export function budgetState(used: number, limit: number): 'full' | 'near' | 'ok' {
  if (used >= limit) return 'full';
  return used >= limit * 0.8 ? 'near' : 'ok';
}

/** The time of day the daily budgets reset: the company's 00:00, on its clock (D-119). */
export function quotaResetTime(locale: string, now = new Date(), timeZone = DEFAULT_COMPANY_TIME_ZONE): string {
  return nextZonedDay(now, timeZone).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', timeZone });
}
